import { Banknote, Clock, FileTextIcon, Store, UserCircle2Icon } from 'lucide-vue-next'
import StatusBadge from 'pejer/src/components/StatusBadge.vue'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'
import { h } from 'vue'
import { Groups, JSSchema } from '.'
import { Environment } from '../types/types'
import { Customer } from './Customer'
import {
  Currency,
  CurrencyList,
  CustomerTypeList,
  MoneyFormatter,
  PaymentEnvironmentFormatter,
  PaymentEnvironmentList,
  PaymentProviderList,
  PaymentStatusFormatter,
  PaymentStatuses,
} from './Global'
import { ColorPalette } from './helpers'

import ToggleDebtCollection from '../components/Payment/ToggleDebtCollection.vue'

export enum PaymentProviders {
  Easy = 2,
  Stripe = 6,
  Dintero = 8,
  KidNorway = 10,
  SwedbankPay = 12,
  bank2bank = 14,
}

export const PaymentProviderMap: Record<string, number> = {
  Easy: PaymentProviders.Easy,
  Stripe: PaymentProviders.Stripe,
  Dintero: PaymentProviders.Dintero,
  KidNorway: PaymentProviders.KidNorway,
  SwedbankPay: PaymentProviders.SwedbankPay,
  bank2bank: PaymentProviders.bank2bank,
}

export type PaymentProvider = keyof typeof PaymentProviders

export function getPaymentProviderData(provider: number): { name: string; logo: string } {
  switch (provider) {
    case PaymentProviders.Easy:
      return {
        name: 'Easy',
        logo: 'easy',
      }
    case PaymentProviders.Stripe:
      return {
        name: 'Stripe',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
      }
    case PaymentProviders.Dintero:
      return {
        name: 'Dintero',
        logo: 'dintero',
      }
    case PaymentProviders.KidNorway:
      return {
        name: 'KidNorway',
        logo: 'kidnorway',
      }
    case PaymentProviders.SwedbankPay:
      return {
        name: 'SwedbankPay',
        logo: 'swedbankpay',
      }
    case PaymentProviders.bank2bank:
      return {
        name: 'Bank2Bank',
        logo: 'bank2bank',
      }
  }
  throw new Error('Unknown payment provider')
}

export namespace Payment {
  export type PaymentStatus =
    | 'InitiatedPayment'
    | 'ReservedPayment'
    | 'ChargedPayment'
    | 'RefundedPayment'
    | 'CancelledPayment'
    | 'FailedPayment'
    | 'RefundInitiated'
    | 'InvoiceConfirmed'

  type PaymentCreatedDto = {
    paymentID: number
    paymentReferenceID: string
    platformID: string
    connectedAccountIDSeller: string | null
    externalPaymentReferenceID: string | null
    externalObjectIDTag: string | null
    environment: Environment
    paymentProvider: PaymentProvider
    paymentGateway: string
    paymentStatus: PaymentStatus
    paymentStatusUpdateTime: string | null
    currency: Currency
    chargeImmediately: boolean
    grossAmount: number
    customerID: number
    successUrl: string | null
    failedUrl: string | null
    insertTime: string
  }

  type Platform_Customer = {
    customerGuid: string
    customerExternalID: string | null
    name: string | null
    email: string | null
    mobilePrefix: string | null
    mobile: string | null
    customerType: Customer.CustomerType
  }

  type PaymentInstructions = {
    info: string | null
    paymentUrl: string | null
    paymentProvider: PaymentProvider
    success: boolean
  }

  type KidNorwayInvoiceDto = {
    kid: string | null
    grossAmount: number
    dueDate: string
    accountNumber: string | null
  }

  type ConnectedAccountLedgerDto = {
    ledgerID: number
    splitID: number
    connectedAccountID: string | null
    platformID: string
    paymentReferenceID: string
    environment: Environment
    currency: Currency
    amount: number
    description: string | null
    insertTime: string
    isSettledPayoutID: number | null
  }

  type PaymentWebhookSubscriptionsDto = {
    eventID: string
    paymentReferenceID: string
    webhookType: PaymentStatus
    url: string | null
    insertTime: string
    isSentTime: string | null
  }

  type PaymentMoneySplitDto = {
    paymentReferenceID: string
    connectedAccount_ExternalReferenceID: string | null
    amount: number
    description: string | null
    insertTime: string
    isFee: boolean
  }

  export type Full = {
    paymentCreated: PaymentCreatedDto
    customer: Platform_Customer
    paymentInstructions: PaymentInstructions
    kidNorwayInvoice: KidNorwayInvoiceDto
    moneySplit: PaymentMoneySplitDto[] | null
    ledger: ConnectedAccountLedgerDto[] | null
    webhooks: PaymentWebhookSubscriptionsDto[] | null
  }

  export interface ListItem {
    paymentID: number
    platformID: string
    paymentReferenceID: string
    connectedAccountIDSeller: string
    externalPaymentReferenceID?: string
    description?: string
    externalObjectIDTag?: string
    environment: Environment
    paymentStatus: PaymentStatus
    paymentStatusUpdateTime: string
    paymentProvider?: string
    currency?: string
    grossAmount: number
    insertTime: string
    customer: {
      customerId: number
      customerExternalID?: string
      customerGuid: string
      customerType?: string
      email?: string
      mobile?: string
      mobilePrefix?: string
      name?: string
    }
    connectedAccount: {
      updateTime: string
      name?: string
      business_TaxID?: string
      countryCode?: string
      bban?: string
      iban?: string
      externalReferenceID?: string
      payoutsEnabled: boolean
      salesEnabled: boolean
      currency?: string
      bankName?: string
      businessName?: string
      businessType?: string
      website?: string
    }
    kidNorwayInvoice?: {
      kid?: string
      grossAmount?: number
      dueDate?: string
      accountNumber?: string
    }
    moneySplitFee: number
    moneySplitNotFee: number
    platformProfit: number
  }
}

export const PaymentGroups = {
  Seller: {
    icon: Store,
    color: ColorPalette.pink,
    mainColumn: 'connectedAccount.name',
  },
  Customer: {
    icon: UserCircle2Icon,
    color: ColorPalette.blue,
    mainColumn: 'customer.name',
  },
  Payment: {
    icon: Banknote,
    color: ColorPalette.green,
    isMainGroup: true,
    mainColumn: 'description',
  },
  'Gross amount': {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'grossAmount',
  },
  Invoice: {
    icon: FileTextIcon,
    color: ColorPalette.orange,
    mainColumn: 'kidNorwayInvoice.kid',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'insertTime',
  },
} as const satisfies Groups<Payment.ListItem>

// I know its ugly but using chatGPT to generate the schema. see index.ts...
export const PaymentListSchema: JSSchema<Payment.ListItem, typeof PaymentGroups> = {
  name: 'Payment',
  title: 'Payments',
  groups: PaymentGroups,
  fields: {
    paymentID: {
      type: 'string',
      humanReadable: 'ID',
      meta: {
        group: 'Payment',
      },
    },
    paymentReferenceID: {
      type: 'string',
      humanReadable: 'Reference ID',
      meta: {
        group: 'Payment',
      },
      formattedCell: (value: string) => {
        return h(
          ColumnLink,
          {
            content: value,
            id: value,
            pathName: 'payment',
          },
          () => value
        )
      },
    },
    platformID: {
      type: 'string',
      humanReadable: 'Platform ID',
      meta: {
        group: 'Payment',
      },
    },
    externalPaymentReferenceID: {
      type: 'string',
      humanReadable: 'External Reference ID',
      meta: {
        group: 'Payment',
      },
    },
    connectedAccountIDSeller: {
      humanReadable: 'Seller ID',
      relation: 'connectedAccounts.connectedAccountID',
      meta: {
        group: 'Seller',
      },
    },
    description: {
      type: 'string',
      humanReadable: 'Description',
      meta: {
        group: 'Payment',
      },
      sensitive: true,
      inSearchSelect: true,
    },
    externalObjectIDTag: {
      type: 'string',
      humanReadable: 'External Object ID Tag',
      meta: {
        group: 'Payment',
      },
    },
    environment: {
      type: 'enum',
      humanReadable: 'Operating Environment',
      options: PaymentEnvironmentList,
      formatter: PaymentEnvironmentFormatter,
      meta: {
        group: 'Payment',
      },
    },
    paymentStatus: {
      type: 'enum',
      humanReadable: 'Status',
      options: PaymentStatuses,
      formatter: PaymentStatusFormatter,
      meta: {
        group: 'Payment',
      },
      formattedCell: (value: Payment.PaymentStatus) => {
        return h(StatusBadge, {
          status: value,
        })
      },
    },
    paymentStatusUpdateTime: {
      type: 'date',
      humanReadable: 'Payment Status Update Time',
      meta: {
        group: 'Payment',
      },
    },
    paymentProvider: {
      type: 'enum',
      humanReadable: 'Provider',
      options: PaymentProviderList,
      meta: {
        group: 'Payment',
      },
    },
    currency: {
      type: 'enum',
      humanReadable: 'Currency',
      options: CurrencyList,
      meta: {
        group: 'Gross amount',
      },
    },
    grossAmount: {
      type: 'number',
      humanReadable: 'Total Amount',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Gross amount',
      },
    },
    paymentGateway: {
      type: 'string',
      humanReadable: 'Gateway',
      optional: true,
      metaType: 'badge',
      meta: {
        group: 'Payment',
      },
    },
    kidNorwayInvoice: {
      humanReadable: 'KID (Norway)',
      meta: {
        group: 'Invoice',
      },
      type: 'object',
      values: {
        kid: {
          type: 'string',
          humanReadable: 'KID',
          meta: {
            group: 'Invoice',
          },
          sensitive: true,
          specificCell:
            () =>
            ({ row }) => {
              return h(
                'span',
                {
                  class: row.original.kidNorwayInvoice.kid ? '' : 'text-slate-500 italic text-xs',
                },
                row.original.kidNorwayInvoice.kid ?? 'Not a KID-payment'
              )
            },
        },
        paidAmount: {
          type: 'number',
          humanReadable: 'Paid Amount',
          formatter: MoneyFormatter,
          metaType: 'money',
          meta: {
            group: 'Invoice',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                'span',
                {
                  class: row.original.kidNorwayInvoice.kid ? '' : 'text-slate-500 italic text-xs',
                },
                row.original.kidNorwayInvoice.kid
                  ? (row.original.kidNorwayInvoice.paidAmount ?? 0) + ' kr'
                  : 'Not a KID-payment'
              )
            },
        },
        dueDate: {
          type: 'date',
          humanReadable: 'Due Date',
          meta: {
            group: 'Invoice',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                'span',
                {
                  class: row.original.kidNorwayInvoice.dueDate
                    ? ''
                    : 'text-slate-500 italic text-xs',
                },
                row.original.kidNorwayInvoice.dueDate ?? 'Not a KID-payment'
              )
            },
        },
        accountNumber: {
          type: 'string',
          humanReadable: 'Account Number',
          meta: {
            group: 'Invoice',
          },
          sensitive: true,
          specificCell:
            () =>
            ({ row }) => {
              return h(
                'span',
                {
                  class: row.original.kidNorwayInvoice.accountNumber
                    ? ''
                    : 'text-slate-500 italic text-xs',
                },
                row.original.kidNorwayInvoice.accountNumber ?? 'Not a KID-payment'
              )
            },
        },
        debtCollectionActivated: {
          type: 'boolean',
          humanReadable: 'Debt Collection Activated',
          optional: true,
          specificCell:
            () =>
            ({ row }) => {
              if (row.original.kidNorwayInvoice.kid)
                return h(ToggleDebtCollection, {
                  paymentReferenceID: row.original.paymentReferenceID,
                  debtCollectionActivated: row.original.kidNorwayInvoice.debtCollectionActivated,
                })

              return h(
                'span',
                {
                  class: 'text-slate-500 italic text-xs',
                },
                'Not a KID-payment'
              )
            },
        },
      },
    },
    customer: {
      humanReadable: 'Customer',
      meta: {
        group: 'Customer',
      },
      type: 'object',
      values: {
        customerId: {
          humanReadable: 'ID',
          // relation: 'customers.customerId',
          meta: {
            group: 'Customer',
          },
        },
        customerExternalID: {
          humanReadable: 'External ID',
          relation: 'customers.customerExternalID',
          meta: {
            group: 'Customer',
          },
        },
        customerGuid: {
          type: 'string',
          humanReadable: 'GUID',
          meta: {
            group: 'Customer',
          },
        },
        customerType: {
          humanReadable: 'Type',
          relation: 'customers.customerType',
          meta: {
            group: 'Customer',
          },
        },
        email: {
          humanReadable: 'Email',
          relation: 'customers.email',
          meta: {
            group: 'Customer',
          },
          sensitive: true,
        },
        mobile: {
          humanReadable: 'Phone',
          relation: 'customers.mobile',
          meta: {
            group: 'Customer',
          },
          sensitive: true,
        },
        mobilePrefix: {
          humanReadable: 'Phone Prefix',
          relation: 'customers.mobilePrefix',
          meta: {
            group: 'Customer',
          },
        },
        name: {
          humanReadable: 'Full name',
          relation: 'customers.name',
          meta: {
            group: 'Customer',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                ColumnLink,
                {
                  content: row.original.customer.name,
                  id: row.original.customer.customerId,
                  pathName: 'customer',
                },
                () => row.original.customer.name
              )
            },
          sensitive: true,
        },
      },
    },
    connectedAccount: {
      humanReadable: 'Seller',
      meta: {
        group: 'Seller',
      },
      type: 'object',
      values: {
        name: {
          humanReadable: 'Account Name',
          relation: 'connectedAccounts.name',
          meta: {
            group: 'Seller',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                ColumnLink,
                {
                  content: row.original.connectedAccount.name,
                  id: row.original.connectedAccountIDSeller,
                  pathName: 'account',
                },
                () => row.original.connectedAccount.name
              )
            },
          sensitive: true,
        },
        bban: {
          relation: 'connectedAccounts.bban',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
        iban: {
          relation: 'connectedAccounts.iban',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
        externalReferenceID: {
          relation: 'connectedAccounts.externalReferenceID',
          meta: {
            group: 'Seller',
          },
        },
        payoutsEnabled: {
          humanReadable: 'Payouts Enabled',
          relation: 'connectedAccounts.payoutsEnabled',
          meta: {
            group: 'Seller',
          },
        },
        salesEnabled: {
          humanReadable: 'Sales Enabled',
          relation: 'connectedAccounts.salesEnabled',
          meta: {
            group: 'Seller',
          },
        },
        currency: {
          humanReadable: 'Currency',
          relation: 'connectedAccounts.currency',
          meta: {
            group: 'Seller',
          },
        },
        bankName: {
          relation: 'connectedAccounts.bankName',
          meta: {
            group: 'Seller',
          },
        },
        businessName: {
          relation: 'connectedAccounts.businessName',
          humanReadable: 'Business Name',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
        business_TaxID: {
          relation: 'connectedAccounts.business_TaxID',
          meta: {
            group: 'Seller',
          },
        },
        countryCode: {
          relation: 'connectedAccounts.countryCode',
          meta: {
            group: 'Seller',
          },
        },
        businessType: {
          relation: 'connectedAccounts.businessType',
          meta: {
            group: 'Seller',
          },
        },
        website: {
          humanReadable: 'Website',
          relation: 'connectedAccounts.website',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
      },
    },
    moneySplitFee: {
      type: 'number',
      humanReadable: 'Money Split Fee',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Payment',
      },
    },
    moneySplitNotFee: {
      type: 'number',
      humanReadable: 'Money Not Included in Split Fee',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Payment',
      },
    },
    platformProfit: {
      type: 'number',
      humanReadable: 'Platform Profit from Transaction',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Payment',
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Created at',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
  },
}

export const PaymentFullSchema: JSSchema<Payment.Full, typeof PaymentGroups> = {
  name: 'PaymentFull',
  title: 'Payment',
  groups: PaymentGroups,
  fields: {
    paymentCreated: {
      type: 'object',
      humanReadable: 'Payment Created',
      meta: {
        group: 'Payment',
      },
      values: {
        paymentID: { type: 'number', humanReadable: 'Payment ID' },
        paymentReferenceID: { type: 'string', humanReadable: 'Payment Reference ID' },
        platformID: { type: 'string', humanReadable: 'Platform ID' },
        connectedAccountIDSeller: { type: 'string', humanReadable: 'Seller ID', optional: true },
        externalPaymentReferenceID: {
          type: 'string',
          humanReadable: 'External Payment Reference ID',
          optional: true,
        },
        externalObjectIDTag: {
          type: 'string',
          humanReadable: 'External Object ID Tag',
          optional: true,
        },
        environment: {
          type: 'enum',
          options: PaymentEnvironmentList,
          humanReadable: 'Environment',
        },
        paymentProvider: {
          type: 'enum',
          options: PaymentProviderList,
          humanReadable: 'Payment Provider',
        },
        paymentGateway: {
          type: 'string',
          humanReadable: 'Payment Gateway',
          optional: true,
        },
        paymentStatus: { type: 'enum', options: PaymentStatuses, humanReadable: 'Payment Status' },
        paymentStatusUpdateTime: {
          type: 'date',
          humanReadable: 'Payment Status Update Time',
          optional: true,
        },
        currency: { type: 'enum', options: CurrencyList, humanReadable: 'Currency' },
        chargeImmediately: { type: 'boolean', humanReadable: 'Charge Immediately' },
        grossAmount: {
          type: 'number',
          metaType: 'money',
          formatter: MoneyFormatter,
          humanReadable: 'Gross Amount',
        },
        customerID: { type: 'number', humanReadable: 'Customer ID' },
        successUrl: { type: 'string', humanReadable: 'Success URL', optional: true },
        failedUrl: { type: 'string', humanReadable: 'Failed URL', optional: true },
        insertTime: { type: 'date', humanReadable: 'Insert Time' },
      },
    },
    customer: {
      type: 'object',
      humanReadable: 'Customer',
      meta: {
        group: 'Customer',
      },
      values: {
        customerGuid: { type: 'string', humanReadable: 'Customer GUID' },
        customerExternalID: {
          type: 'string',
          humanReadable: 'External Customer ID',
          optional: true,
        },
        name: {
          type: 'string',
          humanReadable: 'Name',
          optional: true,
          smartSearchType: 'Lax',
          sensitive: true,
        },
        email: {
          type: 'string',
          humanReadable: 'Email',
          optional: true,
          smartSearchType: 'Lax',
          sensitive: true,
        },
        mobilePrefix: { type: 'string', humanReadable: 'Mobile Prefix', optional: true },
        mobile: { type: 'string', humanReadable: 'Mobile', optional: true, sensitive: true },
        customerType: { type: 'enum', options: CustomerTypeList, humanReadable: 'Customer Type' },
      },
    },
    // paymentInstructions: {
    //   type: 'object',
    //   meta: {
    //     group: 'Payment',
    //   },
    //   humanReadable: 'Payment Instructions',
    //   values: {
    //     info: { type: 'string', humanReadable: 'Information', optional: true },
    //     paymentUrl: {
    //       type: 'string',
    //       humanReadable: 'Payment URL',
    //       optional: true,
    //       sensitive: true,
    //     },
    //     paymentProvider: {
    //       type: 'enum',
    //       options: PaymentProviderList,
    //       humanReadable: 'Payment Provider',
    //     },
    //     success: { type: 'boolean', humanReadable: 'Success' },
    //   },
    // },
    kidNorwayInvoice: {
      type: 'object',
      meta: {
        group: 'Invoice',
      },
      humanReadable: 'KidNorway Invoice',
      values: {
        kid: { type: 'string', humanReadable: 'KID', optional: true, sensitive: true },
        grossAmount: {
          type: 'number',
          metaType: 'money',
          formatter: MoneyFormatter,
          humanReadable: 'Gross Amount',
        },
        dueDate: { type: 'date', humanReadable: 'Due Date' },
        accountNumber: {
          type: 'string',
          humanReadable: 'Account Number',
          optional: true,
          sensitive: true,
        },
        debtCollectionActivated: {
          type: 'boolean',
          humanReadable: 'Debt Collection Activated',
          optional: true,
          specificCell:
            () =>
            ({ row }) => {
              console.log('row.original.paymentReferenceID', row.original)
              if (row.original.kidNorwayInvoice.kid)
                return h(ToggleDebtCollection, {
                  paymentReferenceID: row.original.paymentCreated.paymentReferenceID,
                  debtCollectionActivated: row.original.kidNorwayInvoice.debtCollectionActivated,
                })

              return h(
                'span',
                {
                  class: 'text-slate-500 italic text-xs',
                },
                'Not a KID-payment'
              )
            },
        },
      },
    },

    // moneySplit: {
    //   type: 'array',
    //   meta: {
    //     group: 'Payment',
    //   },
    //   humanReadable: 'Money Split',
    //   values: {
    //     paymentReferenceID: { type: 'string', humanReadable: 'Payment Reference ID' },
    //     connectedAccount_ExternalReferenceID: {
    //       type: 'string',
    //       humanReadable: 'Connected Account External Reference ID',
    //       optional: true,
    //     },
    //     amount: {
    //       type: 'number',
    //       metaType: 'money',
    //       formatter: MoneyFormatter,
    //       humanReadable: 'Amount',
    //     },
    //     description: {
    //       type: 'string',
    //       humanReadable: 'Description',
    //       optional: true,
    //       smartSearchType: 'Lax',
    //       sensitive: true,
    //     },
    //     insertTime: { type: 'date', humanReadable: 'Insert Time' },
    //     isFee: { type: 'boolean', humanReadable: 'Is Fee' },
    //   },
    //   optional: true,
    // },
    // ledger: {
    //   type: 'array',
    //   meta: {
    //     group: 'Payment',
    //   },
    //   humanReadable: 'Ledger',
    //   values: {
    //     ledgerID: { type: 'number', humanReadable: 'Ledger ID' },
    //     splitID: { type: 'number', humanReadable: 'Split ID' },
    //     connectedAccountID: {
    //       type: 'string',
    //       humanReadable: 'Connected Account ID',
    //       optional: true,
    //     },
    //     platformID: { type: 'string', humanReadable: 'Platform ID' },
    //     paymentReferenceID: { type: 'string', humanReadable: 'Payment Reference ID' },
    //     environment: {
    //       type: 'enum',
    //       options: PaymentEnvironmentList,
    //       humanReadable: 'Environment',
    //     },
    //     currency: { type: 'enum', options: CurrencyList, humanReadable: 'Currency' },
    //     amount: {
    //       type: 'number',
    //       metaType: 'money',
    //       formatter: MoneyFormatter,
    //       humanReadable: 'Amount',
    //     },
    //     description: {
    //       type: 'string',
    //       humanReadable: 'Description',
    //       optional: true,
    //       smartSearchType: 'Lax',
    //       sensitive: true,
    //     },
    //     insertTime: { type: 'date', humanReadable: 'Insert Time' },
    //     isSettledPayoutID: {
    //       type: 'number',
    //       humanReadable: 'Is Settled Payout ID',
    //       optional: true,
    //     },
    //   },
    //   optional: true,
    // },
    // webhooks: {
    //   meta: {
    //     group: 'Payment',
    //   },
    //   type: 'array',
    //   humanReadable: 'Webhooks',
    //   values: {
    //     eventID: { type: 'string', humanReadable: 'Event ID' },
    //     paymentReferenceID: { type: 'string', humanReadable: 'Payment Reference ID' },
    //     webhookType: { type: 'enum', options: PaymentStatuses, humanReadable: 'Webhook Type' },
    //     url: { type: 'string', humanReadable: 'URL', optional: true },
    //     insertTime: { type: 'date', humanReadable: 'Insert Time' },
    //     isSentTime: { type: 'date', humanReadable: 'Is Sent Time', optional: true },
    //   },
    //   optional: true,
    // },
  },
}
